import React, { useState, useEffect } from 'react';
import './SignInPage.css';

const SignInPage = ({ onSignIn, onShowGame }) => {
  const [currentQRCode, setCurrentQRCode] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isJoiningGroup, setIsJoiningGroup] = useState(false);
  const [error, setError] = useState(null);
  const [showRegistration, setShowRegistration] = useState(false);
  const [registrationForm, setRegistrationForm] = useState({
    firstName: '',
    lastName: '',
    phone: ''
  });

  const API_BASE = process.env.REACT_APP_API_URL;
  // Handle group joining logic (join existing or create new)
  const handleGroupJoin = async (device) => {
    setIsJoiningGroup(true);
    try {
      console.log('SignInPage: Handling group join for device:', device.id);
      
      // First, try to find an existing waiting group with space
      const response = await fetch(`${API_BASE}/api/groups`);
      const data = await response.json();
      
      if (data.success && data.groups.length > 0) {
        console.log(`SignInPage: Found ${data.groups.length} total groups`);
        
        // Find available groups (waiting status and has space)
        const availableGroups = data.groups
          .filter(group => 
            group.status === 'waiting' && 
            group.current_players < group.max_players
          )
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); // Newest first
        
        console.log(`SignInPage: Found ${availableGroups.length} available groups with space`);
        
        if (availableGroups.length > 0) {
          // Join the latest available group
          const targetGroup = availableGroups[0];
          console.log('SignInPage: Joining existing group:', targetGroup.name);
          
          const joinResponse = await fetch(`${API_BASE}/api/groups/${targetGroup.group_id}/join`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              deviceId: device.id
            })
          });
          
          const joinData = await joinResponse.json();
          
          if (joinData.success) {
            console.log('SignInPage: Successfully joined existing group:', targetGroup.name);
            return {
              success: true,
              groupId: targetGroup.group_id,
              playerInfo: {
                color: joinData.playerColor,
                laneNumber: joinData.laneNumber
              }
            };
          } else {
            console.log('SignInPage: Failed to join existing group:', joinData.error);
            // Continue to create new group if joining fails
          }
        }
      }
      
      // If no available groups, create a new one
      console.log('SignInPage: No available groups, creating new group...');
      const groupName = `Group ${Math.floor(Math.random() * 1000)}`;
      
      const createResponse = await fetch(`${API_BASE}/api/groups/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: groupName,
          maxPlayers: 5
        })
      });
      
      const createData = await createResponse.json();
      
      if (createData.success) {
        console.log('SignInPage: Successfully created new group:', groupName);
        
        // Join the newly created group
        const joinResponse = await fetch(`${API_BASE}/api/groups/${createData.groupId}/join`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            deviceId: device.id
          })
        });
        
        const joinData = await joinResponse.json();
        
        if (joinData.success) {
          console.log('SignInPage: Successfully joined newly created group');
          return {
            success: true,
            groupId: createData.groupId,
            playerInfo: {
              color: joinData.playerColor,
              laneNumber: joinData.laneNumber
            }
          };
        } else {
          console.log('SignInPage: Failed to join newly created group:', joinData.error);
          return { success: false, error: 'Failed to join newly created group' };
        }
      } else {
        console.log('SignInPage: Failed to create group:', createData.error);
        return { success: false, error: 'Failed to create new group' };
      }
    } catch (error) {
      console.error('SignInPage: Error in handleGroupJoin:', error);
      return { success: false, error: 'Network error during group join' };
    } finally {
      setIsJoiningGroup(false);
    }
  };

  // Generate device ID (simple fingerprinting)
  const getDeviceId = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Device fingerprint', 2, 2);
    
    const fingerprint = canvas.toDataURL();
    const userAgent = navigator.userAgent;
    const screenRes = `${window.screen.width}x${window.screen.height}`;
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Create a simple hash
    let hash = 0;
    const str = fingerprint + userAgent + screenRes + timeZone;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return `device_${Math.abs(hash)}`;
  };

  // Get next QR code
  const getNextQRCode = async () => {
    setIsLoading(true);
    setError(null);
    console.log(API_BASE);
    try {
      const response = await fetch(`${API_BASE}/api/qr/next`);
      const data = await response.json();
      
      if (data.success) {
        setCurrentQRCode(data);
        setQrCodeData(data.qrCode);
      } else {
        setError(data.error || 'Failed to get QR code');
      }
    } catch (error) {
      setError('Network error: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Validate QR code
  const validateQRCode = async (codeId) => {
    const deviceId = getDeviceId();
    
    try {
      const response = await fetch(`${API_BASE}/api/qr/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ codeId, deviceId })
      });
      
      const data = await response.json();
      
      if (data.success) {
        if (data.needsRegistration) {
          setShowRegistration(true);
        } else {
          // Device already registered, check if they've won
          if (data.device.hasWon) {
            setError('This device has already won the game!');
            return;
          }
          
          // Handle group joining for existing users
          const groupResult = await handleGroupJoin(data.device);
          if (groupResult.success) {
            // Start game with group info
            onSignIn({
              ...data.device,
              groupId: groupResult.groupId,
              playerInfo: groupResult.playerInfo
            });
            onShowGame();
          } else {
            setError(groupResult.error || 'Failed to join group');
          }
        }
      } else {
        setError(data.error || 'Invalid QR code');
      }
    } catch (error) {
      setError('Network error: ' + error.message);
    }
  };

  // Handle registration form submission
  const handleRegistration = async (e) => {
    e.preventDefault();
    
    if (!registrationForm.firstName || !registrationForm.lastName || !registrationForm.phone) {
      setError('Please fill in all fields');
      return;
    }
    
    const deviceId = getDeviceId();
    
    try {
      const response = await fetch(`${API_BASE}/api/device/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          deviceId,
          firstName: registrationForm.firstName,
          lastName: registrationForm.lastName,
          phone: registrationForm.phone
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Registration successful, handle group joining
        const groupResult = await handleGroupJoin({
          id: deviceId,
          firstName: registrationForm.firstName,
          lastName: registrationForm.lastName,
          phone: registrationForm.phone,
          hasWon: false
        });
        
        if (groupResult.success) {
          // Start game with group info
          onSignIn({
            id: deviceId,
            firstName: registrationForm.firstName,
            lastName: registrationForm.lastName,
            phone: registrationForm.phone,
            hasWon: false,
            groupId: groupResult.groupId,
            playerInfo: groupResult.playerInfo
          });
          onShowGame();
        } else {
          setError(groupResult.error || 'Failed to join group');
        }
      } else {
        setError(data.error || 'Registration failed');
      }
    } catch (error) {
      setError('Network error: ' + error.message);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRegistrationForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Auto-refresh QR code every 30 seconds
  useEffect(() => {
    getNextQRCode();
    
    const interval = setInterval(() => {
      getNextQRCode();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Simulate QR code scan (for testing)
  const simulateScan = () => {
    if (currentQRCode) {
      validateQRCode(currentQRCode.codeId);
    }
  };

  return (
    <div className="signin-page">
      <div className="signin-container">
        <div className="signin-header">
          <h1>🚗 Welcome to Car Racing Game!</h1>
          <p>Scan the QR code below to start playing</p>
        </div>

        <div className="qr-section">
          {isLoading ? (
            <div className="loading">
              <div className="spinner"></div>
              <p>Loading QR code...</p>
            </div>
          ) : currentQRCode ? (
            <div className="qr-display">
              <img 
                src={qrCodeData} 
                alt="QR Code" 
                className="qr-code"
                onClick={simulateScan}
                title="Click to simulate scan (for testing)"
              />
              <div className="qr-info">
                <p><strong>Code ID:</strong> {currentQRCode.codeId}</p>
                <p><strong>Status:</strong> Available</p>
                <button 
                  className="btn btn-secondary"
                  onClick={simulateScan}
                >
                  Simulate Scan (Test)
                </button>
              </div>
            </div>
          ) : (
            <div className="no-qr">
              <p>No QR code available</p>
              <button className="btn btn-primary" onClick={getNextQRCode}>
                Get QR Code
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="error-message">
            {error}
            <button 
              className="btn btn-secondary"
              onClick={() => setError(null)}
            >
              Dismiss
            </button>
          </div>
        )}

        {isJoiningGroup && (
          <div className="loading-message">
            <div className="spinner"></div>
            <p>Joining game group...</p>
          </div>
        )}

        {showRegistration && (
          <div className="registration-modal">
            <div className="registration-content">
              <h2>Welcome! Please Register</h2>
              <p>This is your first time playing. Please provide your details:</p>
              
              <form onSubmit={handleRegistration}>
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={registrationForm.firstName}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your first name"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={registrationForm.lastName}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your last name"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={registrationForm.phone}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your phone number"
                  />
                </div>
                
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    Start Playing
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowRegistration(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="signin-footer">
          <p>Each QR code can only be used once</p>
          <p>New QR codes appear automatically every 30 seconds</p>
        </div>
      </div>
    </div>
  );
};

export default SignInPage;
